import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import { parseISO } from 'date-fns/parseISO';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'wouter';

import {
    CommentsPageResponse,
    CommentsQuery,
    useBlockComment,
    useCommentsList,
    useDestroyComment,
    usePinComment,
    useReactComment,
    useRestoreComment,
} from '../../hooks/useComments';

import { useHasPermission } from '../../contexts/AuthContext';
import { useSetting } from '../../contexts/SiteContext';
import BasicButton from '../buttons/BasicButton';
import ReactionButton from '../buttons/ReactionButton';
import RoundedButton from '../buttons/RoundedButton';
import CommentForm from '../forms/CommentForm';
import PinnedIcon from '../icons/PinnedIcon';
import CommentAvatar from '../partials/CommentAvatar';
import Time from '../partials/Time';
import IdentifyModal from '../popups/IdentifyModal';

import styles from '../../styles/cards/comment-card.module.css';

interface CommentCardProps {
    id?: string;
    author?: CommentAuthor;
    body?: string | null;
    createdAt?: string | null;
    isReply?: boolean;
    isDeleted?: boolean;
    isBlocked?: boolean;
    isPinned?: boolean;
    isHidden?: boolean;
    replyCount?: number;
    reactions?: {
        [key: string]: number;
    } | null;
    replies?: CommentsPageResponse | null;
    commentableKey: string | null;
    query?: CommentsQuery | null;
    onUpdated?: (comment: Comment) => void | null;
    placeholder?: boolean;
    className?: string | null;
}

function CommentCard({
    id = null,
    author,
    body,
    createdAt,
    isReply = false,
    isDeleted = false,
    isBlocked = false,
    isPinned = false,
    isHidden = false,
    replyCount = 0,
    reactions = null,
    replies = null,
    commentableKey = null,
    query = null,
    onUpdated = null,
    placeholder = false,
    className = null,
}: CommentCardProps) {
    const commentsUpdateTimeLimit = useSetting('commentsUpdateTimeLimit', 60);
    const url = useUrlGenerator();
    const user = useUser();
    const canManageComments = useHasPermission('manage_comments');
    const { id: userId } = user || {};
    const tracking = useTracking();
    const { userId: authorUserId, name, author: authorSnippet, verified = false } = author;
    const { slug: authorSlug = null } = authorSnippet || {};
    const [repliesOpened, setRepliesOpened] = useState(replies !== null);
    const [replyOpened, setReplyOpened] = useState(false);
    const [loginModalOpened, setLoginModalOpened] = useState(false);
    const onClickReply = () => {
        if (user === null) {
            setLoginModalOpened(true);
            return;
        }
        tracking.trackEvent('Comments', 'open_reply');
        setRepliesOpened(true);
        if (!isHidden && !isDeleted && !isBlocked) {
            setReplyOpened(!replyOpened);
        }
    };
    const isOwner = userId === authorUserId;
    const canReply = !isReply;
    const createdAtDate = createdAt !== null ? parseISO(createdAt) : null;
    const canEdit =
        createdAtDate !== null
            ? Math.abs(differenceInMinutes(new Date(), createdAtDate)) <= commentsUpdateTimeLimit
            : false;
    const isActive = !isHidden && !isDeleted && !isBlocked;
    const { reactAsync } = useReactComment(id);
    const { blockAsync } = useBlockComment(id);
    const { pinAsync } = usePinComment(id);
    const { destroyAsync } = useDestroyComment(id);
    const { restoreAsync } = useRestoreComment(id);
    const {
        items = [],
        isFetching = false,
        endIsReached = false,
        isLoading = false,
        fetchNextPage,
        addComment,
    } = useCommentsList(
        commentableKey,
        {
            parent: id,
            ...query,
        },
        {
            enabled: repliesOpened && canReply,
            withOffset: true,
            ...(replies !== null
                ? {
                      initialData: {
                          pages: [replies],
                          pageParams: [replies.pagination.page],
                      },
                  }
                : {}),
        },
    );

    const onClickLoadMore = () => {
        fetchNextPage();
    };

    const onReplyComplete = (newComment, parentComment = null) => {
        tracking.trackEvent('Comments', 'reply', null, null, {
            parent: parentComment,
            comment: newComment,
        });
        addComment(newComment);
        if (parentComment !== null && onUpdated !== null) {
            onUpdated(parentComment);
        }
    };

    const onReplyUpdated = (newComment) => {
        addComment(newComment);
    };

    const onClickReaction = () => {
        if (user === null) {
            setLoginModalOpened(true);
            return false;
        }
        return true;
    };

    const onSelectReaction = (newReaction) => {
        reactAsync(newReaction).then((updatedComment) => {
            tracking.trackEvent('Comments', 'reaction', null, null, {
                reaction: newReaction,
                comment: updatedComment,
            });
            if (onUpdated !== null) {
                onUpdated(updatedComment);
            }
        });
    };

    const onLoginModalClosed = () => {
        setLoginModalOpened(false);
    };

    const [editing, setEditing] = useState(false);
    const onClickEdit = () => {
        setEditing(true);
    };
    const onEditComplete = (updatedComment) => {
        setEditing(false);
        if (onUpdated !== null) {
            onUpdated(updatedComment);
        }
    };
    const onCancelEdit = () => {
        setEditing(false);
    };

    const onClickPin = () => {
        pinAsync(!isPinned);
    };

    const onClickDelete = () => {
        if (isDeleted && window.confirm('Êtes-vous sûr de vouloir restaurer ce commentaire ?')) {
            restoreAsync();
        } else if (
            !isDeleted &&
            window.confirm('Êtes-vous sûr de vouloir supprimer ce commentaire ?')
        ) {
            destroyAsync();
        }
    };

    const onClickBlock = () => {
        if (
            window.confirm(
                isBlocked
                    ? 'Êtes-vous sûr de vouloir débloquer cet utilisateur ?'
                    : 'Êtes-vous sûr de vouloir bloquer cet utilisateur ?',
            )
        ) {
            blockAsync(!isBlocked);
        }
    };

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.isDeleted]: isDeleted,
                    [styles.isBlocked]: isBlocked,
                    [styles.isHidden]: isHidden,
                    [styles.isPinned]: isPinned,
                    [styles.isVerified]: verified,
                    [styles.isReply]: isReply,
                },
                className,
            ])}
        >
            <CommentAvatar className={styles.avatar} author={author} />
            {editing ? (
                <CommentForm
                    id={id}
                    author={author}
                    commentableKey={commentableKey}
                    body={body || ''}
                    onComplete={onEditComplete}
                    onCancel={onCancelEdit}
                />
            ) : (
                <div className={styles.comment}>
                    <div className={styles.author}>
                        {isPinned ? <PinnedIcon className={styles.pinnedIcon} /> : null}
                        <span>
                            {authorSlug !== null ? (
                                <Link href={url('author', { author: authorSlug })}>{name}</Link>
                            ) : (
                                name
                            )}
                        </span>
                        <small>
                            <Time date={createdAt} className={styles.date} />
                        </small>
                    </div>
                    {isHidden ? (
                        <div className={styles.body}>
                            <FormattedMessage
                                defaultMessage="Ce commentaire a été supprimé par l’utilisateur."
                                description="Hidden comment label"
                            />
                        </div>
                    ) : (
                        <div className={styles.body} dangerouslySetInnerHTML={{ __html: body }} />
                    )}
                    {isActive || canManageComments ? (
                        <div className={styles.footer}>
                            <ReactionButton
                                onClick={onClickReaction}
                                onSelect={onSelectReaction}
                                className={classNames([
                                    styles.tool,
                                    { [styles.inactive]: reactions === null },
                                    styles.reactionButton,
                                ])}
                            >
                                {reactions !== null ? (
                                    <span className={styles.reactions}>
                                        {Object.keys(reactions).map((reaction) => (
                                            <span
                                                className={styles.reaction}
                                                key={`reaction-${reaction}`}
                                            >
                                                <span className={styles.emoji}>{reaction}</span>
                                                <span className={styles.count}>
                                                    {reactions[reaction]}
                                                </span>
                                            </span>
                                        ))}
                                    </span>
                                ) : null}
                            </ReactionButton>
                            {canReply ? (
                                replyCount > 0 && replies === null ? (
                                    <BasicButton
                                        onClick={onClickReply}
                                        className={classNames([styles.tool, styles.replyButton])}
                                    >
                                        <FormattedMessage
                                            defaultMessage="{count} réponse(s)"
                                            values={{ count: replyCount }}
                                            description="Comment reply count"
                                        />
                                    </BasicButton>
                                ) : (
                                    <BasicButton
                                        onClick={onClickReply}
                                        className={classNames([
                                            styles.tool,
                                            styles.inactive,
                                            styles.replyButton,
                                        ])}
                                    >
                                        Répondre
                                    </BasicButton>
                                )
                            ) : null}
                            {canManageComments ? (
                                <BasicButton
                                    onClick={onClickPin}
                                    className={classNames([styles.tool, styles.admin])}
                                >
                                    {isPinned ? 'Désépingler' : 'Épingler'}
                                </BasicButton>
                            ) : null}
                            {isOwner && !isDeleted && !isHidden && canEdit ? (
                                <BasicButton
                                    onClick={onClickEdit}
                                    className={classNames([styles.tool, styles.admin])}
                                >
                                    Modifier
                                </BasicButton>
                            ) : null}
                            {canManageComments || (isOwner && !isDeleted && !isHidden) ? (
                                <BasicButton
                                    onClick={onClickDelete}
                                    className={classNames([styles.tool, styles.admin])}
                                >
                                    {isDeleted ? 'Restaurer' : 'Supprimer'}
                                </BasicButton>
                            ) : null}
                            {canManageComments ? (
                                <BasicButton
                                    onClick={onClickBlock}
                                    className={classNames([styles.tool, styles.admin])}
                                >
                                    {isBlocked ? 'Débloquer' : 'Bloquer'}
                                </BasicButton>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            )}
            {canReply && repliesOpened ? (
                <div className={styles.replies}>
                    {(items || []).map((item) => (
                        <CommentCard
                            key={`comment-${item.id}`}
                            {...item}
                            onUpdated={onReplyUpdated}
                            commentableKey={commentableKey}
                            query={query}
                            className={styles.reply}
                        />
                    ))}
                    {!endIsReached && !isLoading ? (
                        <RoundedButton
                            className={styles.loadMore}
                            onClick={onClickLoadMore}
                            disabled={isFetching}
                        >
                            Charger plus de réponses
                        </RoundedButton>
                    ) : null}
                    {replyOpened ? (
                        <div
                            className={classNames([
                                styles.form,
                                {
                                    [styles.withReplies]: (items || []).length > 0,
                                },
                            ])}
                        >
                            <CommentForm
                                commentableKey={commentableKey}
                                parent={id}
                                focused
                                onComplete={onReplyComplete}
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}
            {loginModalOpened ? (
                <IdentifyModal
                    title="Pour commenter..."
                    source={`comments:${commentableKey}`}
                    description="Vous devez avoir un compte"
                    withVerification
                    onClosed={onLoginModalClosed}
                />
            ) : null}
        </div>
    );
}

export default CommentCard;
